import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { combineLatest, Observable, Subject, takeUntil, withLatestFrom } from "rxjs";
import { Constants, amountTypeDict, fedTaxErrorMap, stateTaxErrorMap } from "../../utilities/Constants";
import { FormControl } from "@angular/forms";
import { Store } from "@ngrx/store";
import { selectAmountType, selectCheckFrequency, selectTaxWithholdings, selectInputAmount } from "../../../create-request/reducers";
import { currencyStringToNumber } from "../../../create-request/utilities/currencyStringToNumber";
import { ValidateInputService } from "../../services/validate-input.service";
import { numberOrDecimalOnly, percentAllowedChars } from "../../utilities/regex-patterns";
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { selectStandingAuthACHTaxWithholdings, selectStandingAuthAchInputAmount, selectStandingAuthAchInputAmountType } from "../../../standing-authorizations/reducers";

@Component({
  standalone: false,
  selector: 'mm-twe-calculator-modal',
  templateUrl: './twe-calculator-modal.component.html',
  styleUrls: ['./twe-calculator-modal.component.scss']
})
export class TweCalculatorModalComponent {
  @Input() showModal$: Observable<boolean>;
  @Input() isFedTax$: Observable<boolean>;
  @Output() onApplyWithholdings = new EventEmitter<{
    fedTaxCalculatedPercent: string;
    stateTaxCalculatedPercent: string;
  }>();
  destroy$: Subject<boolean> = new Subject();
  modal: EverestModal;
  modalId: string;
  showFedTaxPrompt: boolean = false;
  fedTaxPrompt: string = Constants.tweCalculatorFedTaxPrompt;
  netAmountTypeWarning: string;
  amountControl: FormControl<number>;
  transactionAmount: number;
  isFedTaxCalculator: boolean = false;
  percentAllowedChars = percentAllowedChars;
  calculatedTaxPercent: FormControl<number|string>;
  actualAmount: string;
  isNet: boolean;
  defaultStateTaxPercent: number;
  defaultFedTaxPercent: number;
  isOneTimeCheck: boolean = false;

  constructor(private store: Store, public validationService: ValidateInputService,  private readonly changeDetectorRef: ChangeDetectorRef) {}
  
  ngOnInit(): void {
    this.modalId = 'twe-calculator-modal-' + new Date().getTime().toString();
    this.amountControl = new FormControl();
    this.calculatedTaxPercent = new FormControl();

    this.store.select(selectCheckFrequency).pipe(takeUntil(this.destroy$)).subscribe(checkFrequency => {
      this.isOneTimeCheck = checkFrequency === 'onetime'
    });

    this.isFedTax$.pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectAmountType), this.store.select(selectStandingAuthAchInputAmountType) )
      )
      .subscribe(([isFedTax, amountType, standingAuthAchInputAmountType]) => {
      this.isFedTaxCalculator = this.showFedTaxPrompt = isFedTax;
        if (amountType) {
          this.isNet = amountType === amountTypeDict.net;
        }
        else {
          this.isNet = standingAuthAchInputAmountType === amountTypeDict.net;
        }
      this.netAmountTypeWarning = this.isFedTaxCalculator ? Constants.tweCalculatorFedTaxNetWarning : 
        Constants.tweCalculatorStateTaxNetWarning;
      this.calculatedTaxPercent.clearValidators();
      if (this.isOneTimeCheck) {
        this.calculatedTaxPercent.addValidators(isFedTax ? CustomValidators.oneTimeCheckGreaterThan100 : CustomValidators.oneTimeCheckGreaterThan99State);
      }
      else {
        this.calculatedTaxPercent.addValidators(isFedTax ? CustomValidators.greaterThan99FedRequestCheck : CustomValidators.greaterThan99State);
      }
    });

    combineLatest([
      this.store.select(selectTaxWithholdings),
      this.store.select(selectStandingAuthACHTaxWithholdings)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([taxWithholdings, standingAuthTaxWithholdings]) => {      
      if (taxWithholdings.fedTaxPercent && taxWithholdings.stateTaxPercent) {
        this.defaultStateTaxPercent = parseFloat(taxWithholdings.stateTaxPercent);
        this.defaultFedTaxPercent = parseFloat(taxWithholdings.fedTaxPercent);
      }
      else {
        this.defaultStateTaxPercent = parseFloat(standingAuthTaxWithholdings.stateTaxPercent);
        this.defaultFedTaxPercent = parseFloat(standingAuthTaxWithholdings.fedTaxPercent);
      }
    });
  }

  ngAfterViewInit(): void {
    this.modal = document.getElementById(this.modalId) as EverestModal;

    this.showModal$.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      if (show) {
        this.amountControl.reset();
        this.modal.showModal();
        this.modal.focus();
      }
    });

    this.amountControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectInputAmount),
        this.store.select(selectStandingAuthAchInputAmount)
      )
    ).subscribe(([value, inputAmount, standingAuthAchInputAmount]) => {
      if (!value) {
        this.calculatedTaxPercent.patchValue(this.actualAmount = '-');
      } else {
        let validInputAmount= '';
        if(inputAmount !== undefined && inputAmount !== null){
          validInputAmount = inputAmount;
        }
        else {
          validInputAmount = standingAuthAchInputAmount!;
        }
        let transactionAmount = currencyStringToNumber(validInputAmount!);
        let calculatedTaxPercentNumber = this.isFedTaxCalculator ? this.calculateFedTax(+value, transactionAmount) : 
          this.calculateStateTax(+value, transactionAmount);
        const roundedPercentage = this.isFedTaxCalculator ? Math.round(calculatedTaxPercentNumber) : Number.parseFloat(calculatedTaxPercentNumber.toFixed(3));        
        this.calculatedTaxPercent.patchValue(roundedPercentage);
        this.actualAmount = this.calculateActualDollarAmount(roundedPercentage, transactionAmount);
      }
    });

    this.changeDetectorRef.detectChanges();
  }

  get TaxError(): string | undefined {
    if (this.calculatedTaxPercent && this.calculatedTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.calculatedTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        if (this.isFedTaxCalculator) {
          return fedTaxErrorMap[errorKeys[0] as keyof typeof fedTaxErrorMap];
        } else {
          return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onApplyWithholdingClick() {
    this.onApplyWithholdings.emit({ 
      fedTaxCalculatedPercent: this.isFedTaxCalculator ? this.calculatedTaxPercent.value.toString() : '', 
      stateTaxCalculatedPercent: this.isFedTaxCalculator ? '' : this.calculatedTaxPercent.value.toString()
    });
    
    this.hideModal();
  }

  hideModal() {
    this.modal.hideModal(); //in separate function in order to mock it in unit tests
  }

  calculateFedTax(taxAmount: number, totalAmount: number): number {
    let grossTransactionAmount = totalAmount;

    if (totalAmount === 0) {
      return 0;
    }
    if (this.isNet) {
      grossTransactionAmount = this.calculateGrossAmountFromOneTaxRate(taxAmount, this.defaultStateTaxPercent/100, totalAmount);
    }

    let fedTaxPercentage = (taxAmount/grossTransactionAmount)*100;
    return fedTaxPercentage;
  }

  calculateStateTax(taxAmount: number, totalAmount: number): number {
    let grossTransactionAmount = totalAmount;

    if (totalAmount === 0) {
      return 0;
    }
    if (this.isNet) {
      grossTransactionAmount = this.calculateGrossAmountFromOneTaxRate(taxAmount, this.defaultFedTaxPercent/100, totalAmount);
    }

    let stateTaxPercentage = (taxAmount/grossTransactionAmount)*100;
    return stateTaxPercentage;
  }

  /**
   * Calculates the Gross amount from the net transaction amount, the dollar tax amount input of one tax type,
   * and the known decimal tax rate of the other tax type.
   * 
   * Derived from the following equations:
   *  
   * GrossAmount = NetAmount / (1 - (StateTaxRate + FedTaxRate))
   * 
   * TaxRate = TaxDollarAmount / GrossAmount
   * @param taxAmountInput the dollar tax amount of one tax type
   * @param knownTaxRate the tax rate of the other tax type
   * @param netAmount the transaction's net amount
   * @returns the total Gross amount
   */
  calculateGrossAmountFromOneTaxRate(taxAmountInput: number, knownTaxRate: number, netAmount: number): number {
    return (taxAmountInput + netAmount) / (1 - knownTaxRate);
  }

  /**
   * Calculates the Gross amount from the net transaction amount, fed tax rate, and state tax rate
   * @param fedTaxRate the known fed tax rate
   * @param stateTaxRate the known state tax rate
   * @param netAmount the transaction's net amount
   * @returns the total Gross amount
   */
  calculateGrossAmountFromBothTaxRates(fedTaxRate: number, stateTaxRate: number, netAmount: number): number {
    return netAmount / (1 - (fedTaxRate + stateTaxRate));
  }
  
  calculateActualDollarAmount(percentage: number, totalAmount: number): string {
    let grossTransactionAmount = totalAmount;
    if (this.isNet) {
      let fedTaxRate = this.isFedTaxCalculator ? percentage/100 : this.defaultFedTaxPercent/100;
      let stateTaxRate = this.isFedTaxCalculator ? this.defaultStateTaxPercent/100 : percentage/100;
      grossTransactionAmount = this.calculateGrossAmountFromBothTaxRates(fedTaxRate, stateTaxRate, totalAmount);
    }

    let actualAmount = grossTransactionAmount*(percentage/100);    
    return (Math.round(actualAmount*100)/100).toFixed(2);
  }

  onPaste(event: ClipboardEvent) {
    //block non-numeric inputs
    if (event.clipboardData != undefined) {
      let pastedInput = event.clipboardData.getData('text/plain');
      if (!numberOrDecimalOnly.test(pastedInput)) {
        event.preventDefault();
      }
    }
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {};
  hideModal: () => {};
};